import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Box } from 'grommet';
import {
  Seo, UnderConstruction, ProductRowStats, SubmissionLoader,
} from '@Components/Control';
import { useAppTheme } from '@Components/Context';
import { AuthNavWrapper } from '@Components/Navigation';
import { WidgetRenderer } from '@Components/Partial/Product/Dashboard';
import { withProductAuth } from '@Components/Layout';
import {
  handleQuery,
  handleSort,
  handleFilter,
  initializeReviewedRows,
} from '@Helpers/controlFunctions';
import {
  initiateResultsRemovalsRequest,
  clearResultsRemovalsRequest,
  initiateResultsInfringementsMetaRequest,
  clearResultsInfringementsMetaRequest,
  initiateLiveTrackerAnalyticsRequest,
  clearLiveTrackerAnalyticsRequest,
} from '@Actions';


const ConfirmedRemovalsPage = ({
  small,
  location,
  size,
  loading,
  submitLoading,
  fetchConfirmedRemovals,
  clearConfirmedRemovals,
  fetchConfirmedRemovalsMeta,
  clearConfirmedRemovalsMeta,
  confirmedRemovalsData = null,
  confirmedRemovalsMeta = null,
  resultsDisabled = false,
  paymentPending = false,
  hostingData = null,
  fetchHostingData,
  clearHostingData,
}) => {
  const {
    theme: { page },
    constructionImage,
    isDarkMode,
    disableResultsFeatures,
  } = useAppTheme();
  const [reviewedData, setReviewedData] = React.useState([]);
  const [query, setQuery] = React.useState({
    paging: {
      to: 1,
      size: 20,
    },
  });

  React.useEffect(() => {
    fetchHostingData();
    return () => clearHostingData();
  }, []);

  React.useEffect(() => {
    /* eslint-disable camelcase */
    fetchConfirmedRemovalsMeta({ page_uid: 'successful_removals' });
    /* eslint-enable camelcase */
    fetchConfirmedRemovals({ ...query });

    return () => {
      clearConfirmedRemovalsMeta();
      clearConfirmedRemovals();
    };
  }, [query]);

  const handleExportCSV = () => {
    fetchConfirmedRemovals({ ...query }, true);
  };

  const padding = small ? '1.5rem' : '2rem';
  const totalResults = confirmedRemovalsData?.totalCount ?? 0;
  const resultsPerPageOptions = [10, 15, 20, 25, 50];
  const underMaintenance = (disableResultsFeatures || resultsDisabled);
  const usePageBg = isDarkMode ? page.background : page.altBg;

  const renderPageContent = () => {
    if (underMaintenance) {
      return (
        <Box flex align="center" justify="center" background={usePageBg}>
          <UnderConstruction
            imageUrl={constructionImage}
            header="Under Maintenance"
            message="System currently undergoing maintenance. Please check back later."
          />
        </Box>
      );
    }

    return (
      <Box flex background={usePageBg} pad={{ top: '0', bottom: padding }}>
        <Box width="100%" direction="row" justify="end" pad={{ vertical: '0.65rem', horizontal: '1.5rem' }}>
          <ProductRowStats
            isLast
            key="totalRemovals"
            small={small}
            label="Total Removals:"
            stat={totalResults}
            color={page.primaryText}
          />
        </Box>
        <WidgetRenderer
          hideValueWidgets
          small={small}
          size={size}
          loading={loading}
          handleQuery={(parentKey, updateKey, value) => (
            handleQuery(query, setQuery, parentKey, updateKey, value, {
              paging: {
                to: 1,
                size: 20,
              },
            })
          )}
          handleSort={(sortValue, sortDirection) => (
            handleSort(query, setQuery, sortValue, sortDirection)
          )}
          handleFilter={(key, value, predicate, seperateValues, reset) => (
            handleFilter(query, setQuery, key, value, predicate, seperateValues, reset)
          )}
          filterSortMeta={confirmedRemovalsMeta}
          searchQuery={query}
          widgetData={confirmedRemovalsData?.data}
          padding={padding}
          selectedStatuses={reviewedData}
          initializeReviewedRows={(data) => (
            initializeReviewedRows(setReviewedData, data)
          )}
          updateRowReviewStatus={() => null}
          updateMultipleRowsReviewStatus={() => null}
          handleExportCSV={handleExportCSV}
          resultsPerPageOptions={resultsPerPageOptions}
          resultsPerPage={query.paging?.size}
          currentPage={query.paging?.to}
          totalResults={totalResults}
          noDataMessage="No confirmed removals found. Review results from the All Infringements page, the Review Tool, or by clicking into details from the All Searches page and check back soon"
          paymentPending={paymentPending}
          hostingData={hostingData?.data}
          includeISPData
        />
      </Box>
    );
  };

  return (
    <AuthNavWrapper
      small={small}
      location={location}
      bannerProps={{
        title: 'Confirmed removals',
        subTitle: 'Results',
      }}
    >
      <Seo />
      {submitLoading && (
        <SubmissionLoader
          textColor={page.modalText}
          loaderColor={page.highlight}
          message="Fetching CSV export..."
          contentWidth="20rem"
        />
      )}
      {renderPageContent()}
    </AuthNavWrapper>
  );
};

function mapStateToProps(state) {
  return {
    confirmedRemovalsData: state.productResults.confirmedRemovals,
    confirmedRemovalsMeta: state.productResults.infringementsMeta,
    loading: state.fetchLoader.dataLoading,
    submitLoading: state.fetchLoader.submissionLoading,
    hostingData: state.analytics.liveTrackerData,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    fetchConfirmedRemovals: initiateResultsRemovalsRequest,
    clearConfirmedRemovals: clearResultsRemovalsRequest,
    fetchConfirmedRemovalsMeta: initiateResultsInfringementsMetaRequest,
    clearConfirmedRemovalsMeta: clearResultsInfringementsMetaRequest,
    fetchHostingData: initiateLiveTrackerAnalyticsRequest,
    clearHostingData: clearLiveTrackerAnalyticsRequest,
  }, dispatch);
}

ConfirmedRemovalsPage.propTypes = {
  small: PropTypes.bool.isRequired,
  size: PropTypes.oneOf(['xsmall', 'small', 'medium', 'mediumlarge', 'large']).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  submitLoading: PropTypes.bool.isRequired,
  fetchConfirmedRemovals: PropTypes.func.isRequired,
  clearConfirmedRemovals: PropTypes.func.isRequired,
  fetchConfirmedRemovalsMeta: PropTypes.func.isRequired,
  clearConfirmedRemovalsMeta: PropTypes.func.isRequired,
  confirmedRemovalsData: PropTypes.shape({
    totalCount: PropTypes.number.isRequired,
    query: PropTypes.shape({
      paging: PropTypes.shape({
        to: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
        ]).isRequired,
        size: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
        ]).isRequired,
      }),
    }).isRequired,
    data: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.any.isRequired,
      type: PropTypes.string.isRequired,
      statusOptions: PropTypes.arrayOf(PropTypes.shape({
        api: PropTypes.string.isRequired,
        display: PropTypes.string.isRequired,
      })).isRequired,
      unit: PropTypes.string,
      feature: PropTypes.string,
    }).isRequired).isRequired,
  }),
  confirmedRemovalsMeta: PropTypes.shape({
    sort: PropTypes.shape({
      name: PropTypes.shape({
        options: PropTypes.arrayOf(PropTypes.shape({
          name: PropTypes.string.isRequired,
          formatted: PropTypes.string.isRequired,
        }).isRequired).isRequired,
      }).isRequired,
      direction: PropTypes.shape({
        options: PropTypes.arrayOf(PropTypes.shape({
          name: PropTypes.string.isRequired,
          formatted: PropTypes.string.isRequired,
        }).isRequired).isRequired,
      }).isRequired,
    }).isRequired,
    filters: PropTypes.shape({
      options: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string.isRequired,
        formatted: PropTypes.string.isRequired,
        validPredicates: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
      })).isRequired,
    }).isRequired,
  }),
  resultsDisabled: PropTypes.bool,
  paymentPending: PropTypes.bool.isRequired,
  hostingData: PropTypes.shape({ data: PropTypes.array }),
  fetchHostingData: PropTypes.func.isRequired,
  clearHostingData: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductAuth(ConfirmedRemovalsPage));
